import { AfterViewInit, Component, ElementRef, forwardRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AngularEditorConfig, AngularEditorComponent } from '@kolkov/angular-editor';
import { TranslateService } from '@ngx-translate/core';
import { AngularEditorConfigService } from '../../services/angular-editor-config/angular-editor-config.service';
import { BaseInputDirective } from '../base-input/base-input.directive';

@Component({
    selector: 'lib-gung-html-editor',
    templateUrl: './gung-html-editor.component.html',
    styleUrls: ['./gung-html-editor.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GungHtmlEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class GungHtmlEditorComponent extends BaseInputDirective<string> implements OnInit, AfterViewInit {
  fontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 34, 36, 38, 40, 42, 46, 50, 55, 60, 65, 70, 80, 90, 100, 150, 200];
  private savedColorPickerSelection: Range | null = null;

  /* Disable image copy paste / drag and drop to gung html editor */
  @HostListener('paste', ['$event'])
  protected pasteFromClipboard(event: any): void {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');

    // Block image pasting because it will paste in base64 encoded data
    // Only block paste if it contains an image file AND no HTML content
    // Excel copy pasting includes both HTML and an image representation, so we allow it if HTML is present
    const hasHtml = event?.clipboardData?.types?.includes('text/html');
    if (event?.clipboardData?.files?.length && !hasHtml) {
      for (let i = 0; i < event.clipboardData.files.length; i++) {
        if (this.isImageFile(event.clipboardData.files[i])) {
          return;
        }
      }
    }

    // If rawPaste is enabled, the angular-editor will handle the paste event and insert the raw text as HTML, so no tags will be added
    if (!this.angularEditorConfig.rawPaste) {
      if (hasHtml) {
        // if content is html, paste html
        const html = event.clipboardData.getData('text/html');
        window.document.execCommand("insertHTML", false, html);
      } else {
        window.document.execCommand("insertText", false, text);
      }
    }
  }

  @ViewChild('editor') editorComponent: AngularEditorComponent;

  @Input()
  public extraInputClass?: string;

  @Input()
  public labelClass?: string;

  @Input()
  public labelBold = false;

  @Input()
  public horizontal = false;

  @Input()
  public angularEditorConfig: AngularEditorConfig = this.angularEditorConfigService.getDefaultEditorConfig();

  @Input()
  public filteredHeadingNames?: string[];

  @Input()
  public backgroundColor?: string;

  constructor(
    protected translateService: TranslateService,
    protected angularEditorConfigService: AngularEditorConfigService,
    private elementRef: ElementRef
  ) {
    super();
    if (this.labelBold) {
      this.labelClass = (this.labelClass || '') + ' font-weight-bold';
    }
  }

  get editorBgStyle() {
    return {'--gung-editor-bg': this.backgroundColor ? this.backgroundColor : '#fff'};
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    // Default paragraph separator is hard set to div when switching to htmlMode, this is a workaround to set it back to whatever is set in the config
    this.editorComponent.viewMode.subscribe((isViewMode) => {
      if (isViewMode) {
        this.editorComponent.executeCommand('defaultParagraphSeparator', this.angularEditorConfig.defaultParagraphSeparator);
      }
    });

    if (this.filteredHeadingNames && this.filteredHeadingNames.length > 0) {
      this.editorComponent.editorToolbar.headings = this.editorComponent.editorToolbar.headings.filter(h => this.filteredHeadingNames.includes(h.label));
    }

    this.setupColorPickerSelectionPreservation();
  }

  // When <input type="color"> in the toolbar is clicked, the editor loses focus and the browser
  // drops the text selection. This saves the selection before the system color picker opens and
  // restores it (capture phase, before angular-editor's own handler) so execCommand('foreColor')
  // still has a valid selection to apply the color to.
  private setupColorPickerSelectionPreservation(): void {
    setTimeout(() => {
      const textArea: HTMLElement = this.editorComponent.textArea?.nativeElement;
      if (!textArea) return;

      // Save selection when the editor loses focus (fires before selection is dropped)
      textArea.addEventListener('blur', () => {
        const sel = window.getSelection();
        this.savedColorPickerSelection = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
      });

      const colorInputs: NodeListOf<HTMLInputElement> = this.elementRef.nativeElement.querySelectorAll('input[type="color"]');

      colorInputs.forEach((input: HTMLInputElement) => {
        input.addEventListener('change', () => {
          if (!this.savedColorPickerSelection) return;
          this.editorComponent.focus();
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(this.savedColorPickerSelection);
        }, true);
      });
    });
  }

  protected isImageFile(file: File): boolean {
    return file.type.search(/^image\//i) === 0;
  }

  onFontSizeChange(sizePx: string, executeCommandFn: Function) {
    if (!sizePx) return;
    this.editorComponent.focus();

    // 1. Use the native command to handle selection splitting and crossing tag boundaries.
    // The browser is much better at this than manual range manipulation.
    executeCommandFn('fontSize', '7');

    // 2. Find all <font size="7"> tags created by the browser.
    const textArea = this.editorComponent.textArea.nativeElement;
    const fontTags = Array.from(textArea.querySelectorAll('font[size="7"]'));

    if (fontTags.length > 0) {
      fontTags.forEach((font: HTMLElement) => {
        // 3. Create a span with the desired pixel size.
        const span = document.createElement('span');
        span.style.fontSize = `${sizePx}px`;
        
        // 4. If the font tag has parents with inline styles, clear them to prevent overrides.
        let parent = font.parentElement;
        while (parent && parent !== textArea) {
          if (parent.nodeType === 1) {
            const p = parent as HTMLElement;
            // Clear styles that would override or conflict with our new size/look
            if (p.style.fontSize) p.style.fontSize = '';
            if (p.style.fontFamily) p.style.fontFamily = '';
            if (p.style.fontWeight) p.style.fontWeight = '';
            if (p.style.backgroundColor && p.style.backgroundColor !== 'transparent') p.style.backgroundColor = '';
          }
          parent = parent.parentElement;
        }

        // 5. Transfer children and clean nested styles.
        while (font.firstChild) {
          const child = font.firstChild;
          if (child.nodeType === 1) {
            const el = child as HTMLElement;
            el.style.fontSize = '';
            el.style.fontFamily = '';
            el.style.fontWeight = '';
            if (el.style.backgroundColor && el.style.backgroundColor !== 'transparent') el.style.backgroundColor = '';
            
            el.querySelectorAll('*').forEach((nested: HTMLElement) => {
              (nested as HTMLElement).style.fontSize = '';
              (nested as HTMLElement).style.fontFamily = '';
              (nested as HTMLElement).style.fontWeight = '';
              if ((nested as HTMLElement).style.backgroundColor && (nested as HTMLElement).style.backgroundColor !== 'transparent') {
                (nested as HTMLElement).style.backgroundColor = '';
              }
            });
            if (el.tagName === 'FONT') el.removeAttribute('size');
          }
          span.appendChild(child);
        }

        // 6. Replace the marker font tag with our precise span.
        font.parentNode.replaceChild(span, font);
      });

      // 7. Synchronize back to the model.
      this.value = textArea.innerHTML;
    }
  }
}
